import React, { useState, useEffect, useRef } from "react";
import styled from "styled-components";
import PropTypes from "prop-types";
import DatePicker, { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { format } from "date-fns";
import enUS from "date-fns/locale/en-US";
import Chevron from "../../../../../assets/images/icons/Web_Sprite_Icon_Chevron_Grey_analytics.png";

registerLocale("en-US", enUS);

const CustomDatePicker = ({ onChevronClick, onDateChange }) => {
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [startDateSelected, setStartDateSelected] = useState(false);
    const [endDateSelected, setEndDateSelected] = useState(false);
    const datePickerRef = useRef(null);

    const formatDate = (date) => (date ? format(date, "MMM dd, yyyy") : "MMM DD, YYYY");

    const handleStartDateChange = (date) => {
        setStartDate(date);
        setStartDateSelected(false);
        if (endDate) {
            onDateChange(date, endDate);
        }
    };

    const handleEndDateChange = (date) => {
        setEndDate(date);
        setEndDateSelected(false);
        if (startDate) {
            onDateChange(startDate, date);
        }
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (datePickerRef.current && !datePickerRef.current.contains(event.target)) {
                setStartDateSelected(false);
                setEndDateSelected(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    return (
        <PositionWrapper ref={datePickerRef}>
            <DateSelectionContainer>
                <DateSelectionHeader onClick={onChevronClick}>
                    <ImageContainer src={Chevron} width="16px" height="16px" padding="0.926px 0.206px" />
                    <CustomDateText leadingTrim={"both"} textEdge={"cap"}>
                        Custom
                    </CustomDateText>
                </DateSelectionHeader>

                <DateSelectionRow>
                    <LabelContainer>
                        <CustomDateText color={"var(--White---Black-Secondary, #CED1D9)"} fontWeight={"400"}>
                            Start
                        </CustomDateText>
                    </LabelContainer>
                    <DateInputContainer isSelected={startDateSelected}>
                        <CustomDateText
                            onClick={() => {
                                setStartDateSelected(true);
                                setEndDateSelected(false);
                            }}
                            fontWeight={"400"}
                        >
                            {formatDate(startDate)}
                        </CustomDateText>
                    </DateInputContainer>
                </DateSelectionRow>

                <DateSelectionRow>
                    <LabelContainer>
                        <CustomDateText color={"var(--White---Black-Secondary, #CED1D9)"} fontWeight={"400"}>
                            End
                        </CustomDateText>
                    </LabelContainer>
                    <DateInputContainer isSelected={endDateSelected}>
                        <CustomDateText
                            onClick={() => {
                                setEndDateSelected(true);
                                setStartDateSelected(false);
                            }}
                            fontWeight={"400"}
                        >
                            {formatDate(endDate)}
                        </CustomDateText>
                    </DateInputContainer>
                </DateSelectionRow>
            </DateSelectionContainer>

            {startDateSelected && (
                <DatePicker
                    calendarContainer={CalendarContainer}
                    isStartDate
                    selected={startDate}
                    onChange={handleStartDateChange}
                    maxDate={endDate || new Date()}
                    dateFormat="MMM dd, yyyy"
                    inline
                    showMonthDropdown
                    showYearDropdown
                    useShortMonthInDropdown
                    locale="en-US"
                />
            )}

            {endDateSelected && (
                <DatePicker
                    calendarContainer={CalendarContainer}
                    selected={endDate}
                    onChange={handleEndDateChange}
                    minDate={startDate}
                    maxDate={new Date()}
                    dateFormat="MMM dd, yyyy"
                    inline
                    showMonthDropdown
                    showYearDropdown
                    useShortMonthInDropdown
                    locale="en-US"
                />
            )}
        </PositionWrapper>
    );
};

CustomDatePicker.propTypes = {
    onChevronClick: PropTypes.func.isRequired,
    onDateChange: PropTypes.func.isRequired,
};

export default CustomDatePicker;

const PositionWrapper = styled.div`
    position: relative;
    display: flex;
`;

const DateSelectionContainer = styled.div`
    position: absolute;
    top: 10px;
    left: -5vw;
    z-index: 2000;
    max-width: 300px;
    min-width: 280px;
    padding: 14px 12px;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 5px;
    border-radius: 10px;
    border: 1px solid var(--white-white-20, rgba(255, 255, 255, 0.2));
    background: #110e21;
    box-shadow: 0px 4px 12px 0px rgba(0, 0, 0, 0.25);
`;

const DateSelectionHeader = styled.div`
    display: flex;
    padding: 9px 9px;
    align-items: center;
    gap: 4px;
    align-self: stretch;
    cursor: pointer;

    &:hover {
        opacity: 0.7;
    }
`;

const ImageContainer = styled.img`
    display: flex;
    width: ${(props) => (props.width ? props.width : "14px")};
    height: ${(props) => (props.height ? props.height : "14px")};
    padding: ${(props) => (props.padding ? props.padding : "1px 1px")};
    justify-content: center;
    align-items: center;
    cursor: pointer;
    transition: transform 0.2s;
`;

const CustomDateText = styled.p`
    color: ${(props) => (props.color ? props.color : "var(--Text-Primary, #FBFCFF)")};
    leading-trim: ${(props) => (props.leadingTrim ? props.leadingTrim : "none")};
    text-edge: ${(props) => (props.textEdge ? props.textEdge : "none")};
    font-family: "Barlow";
    font-size: 16px;
    font-style: normal;
    font-weight: ${(props) => (props.fontWeight ? props.fontWeight : "600")};
    line-height: normal;
    letter-spacing: 0px;
`;

const LabelContainer = styled.div`
    width: 50px;
    text-align: right;
`;

const DateSelectionRow = styled.div`
    display: flex;
    padding: 10px 22px;
    align-items: center;
    gap: 14px;
    align-self: stretch;
`;

const DateInputContainer = styled.div`
    display: flex;
    padding: 6px 14px;
    justify-content: center;
    align-items: center;
    gap: 10px;
    border-radius: 5px;
    border: ${(props) =>
        props.isSelected ? "2px solid var(--Purple-Secondary, #9D75F5)" : "2px solid rgba(255, 255, 255, 0.20)"};
    background: ${(props) => (props.isSelected ? "none" : "rgba(255, 255, 255, 0.10)")};

    &:hover {
        border: 2px solid rgba(255, 255, 255, 0.3);
        background: rgba(255, 255, 255, 0.2);
    }
`;

const CalendarContainer = styled.div`
    position: absolute !important;
    top: 0;
    left: 8vw;
    z-index: 3000;
    border-radius: 10px;
    border: 1px solid var(--white-white-20, rgba(255, 255, 255, 0.2));
    background: #110e21;
    box-shadow: 0px 4px 12px 0px rgba(0, 0, 0, 0.25);

    .react-datepicker {
        position: absolute !important;
        font-family: "Barlow";
    }

    .react-datepicker__month-container {
        background: rgb(0, 0, 0);
        color: var(--Text-Primary, #fbfcff);
        font-family: "Barlow";
    }

    .react-datepicker__header {
        background: #110e21;
        border-bottom: 1px solid rgba(255, 255, 255, 0.2);
        color: var(--Text-Primary, #fbfcff);
    }

    .react-datepicker__current-month {
        color: rgb(255, 255, 255);
        font-family: "Barlow";
        font-size: 16px;
        font-weight: 600;
        padding: 10px 0;
    }

    .react-datepicker__navigation {
        top: 10px;
    }

    .react-datepicker__month-text {
        color: var(--Text-Primary, #fbfcff);
        font-family: "Barlow";
        font-size: 14px;
        padding: 8px;
        margin: 4px;
        border-radius: 4px;
    }

    .react-datepicker__month-text:hover {
        background-color: rgba(255, 255, 255, 0.2);
    }

    .react-datepicker__month--selected,
    .react-datepicker__month--in-range {
        background-color: var(--Purple-Secondary, #9d75f5);
        color: #fbfcff;
        border-radius: 4px;
    }

    .react-datepicker__month--disabled {
        color: rgba(255, 255, 255, 0.3);
        cursor: not-allowed;
    }

    .react-datepicker__year-text {
        color: var(--Text-Primary, #fbfcff);
        font-family: "Barlow";
        font-size: 14px;
        padding: 8px;
        margin: 4px;
        border-radius: 4px;
    }

    .react-datepicker__year-text:hover {
        background-color: rgba(255, 255, 255, 0.2);
    }

    .react-datepicker__year--selected,
    .react-datepicker__year--in-range {
        background-color: var(--Purple-Secondary, #9d75f5);
        color: #fbfcff;
        border-radius: 4px;
    }

    .react-datepicker__year--disabled {
        color: rgba(255, 255, 255, 0.3);
        cursor: not-allowed;
    }

    .react-datepicker__day,
    .react-datepicker__day-name {
        color: var(--Text-Primary, #fbfcff);
    }

    .react-datepicker__day--selected,
    .react-datepicker__day--keyboard-selected {
        background-color: var(--Purple-Secondary, #9d75f5);
        color: #fbfcff;
    }

    .react-datepicker__day:hover {
        background-color: rgba(255, 255, 255, 0.2);
    }

    /* Month and Year Dropdowns */
    .react-datepicker__month-dropdown,
    .react-datepicker__year-dropdown {
        background: #110e21;
        color: #fbfcff;
        border-radius: 8px;
        border: 1px solid rgba(255, 255, 255, 0.15);
        box-shadow: 0px 4px 12px 0px rgba(0, 0, 0, 0.15);
    }

    .react-datepicker__month-dropdown-container,
    .react-datepicker__year-dropdown-container {
        background: #110e21;
        color: #fbfcff;
    }

    .react-datepicker__month-option,
    .react-datepicker__year-option {
        background: #110e21;
        color: #fbfcff;
        padding: 8px 12px;
        cursor: pointer;
        transition: background 0.2s;
    }

    .react-datepicker__month-option--selected,
    .react-datepicker__year-option--selected {
        // background-color: var(--Purple-Secondary, #9d75f5);
        color: #9d75f5;
    }

    .react-datepicker__month-option:hover,
    .react-datepicker__year-option:hover {
        background-color: rgba(157, 117, 245, 0.2);
    }

    .react-datepicker__month-read-view,
    .react-datepicker__year-read-view {
        background: #110e21;
        color: #fbfcff;
        border-radius: 6px;
        padding: 6px 12px;
        cursor: pointer;
    }

    .react-datepicker__month-read-view--down-arrow,
    .react-datepicker__year-read-view--down-arrow {
        margin-top: 6px;
        margin-right: 3px;
        border-top-color: #fbfcff;
    }
`;
